import { createError, useRoute } from '#imports'
import { type Ref, computed } from 'vue'

import {
  type HttpRequestUrlParameters,
  linkBoosterAPI,
  productAPI,
  productReviewsAPI,
} from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { MAX_REVIEWS_DISPLAYED_IN_LIST } from '~/scopes/reviews/constants'

import translations from '../pages/Product.translations'

export async function useProductRequests(
  productId: string,
  grade: Ref<number | null>,
  offerType: Ref<number | null>,
) {
  const i18n = useI18n()
  const route = useRoute()
  const experiments = useExperiments()

  const requestParams = { pathParams: { productId } }

  const {
    getProduct,
    getBestOffers,
    getBreadcrumb,
    getEcoBlock,
    getPickers,
    getTechnicalSpecifications,
  } = productAPI
  const { getSeoTags } = linkBoosterAPI
  const { getRating, getReviews, getRatingDistribution } = productReviewsAPI

  const pickersQueryParams = computed(() => {
    return removeEmptyValuesInObject({
      grade: grade.value ? String(grade.value) : null,
      special_offer_type: offerType.value ? String(offerType.value) : null,
      top_to_bottom:
        experiments['experiment.ppMixAndMatch'] === 'withMixAndMatch'
          ? 'enabled'
          : 'disabled',
    }) as HttpRequestUrlParameters
  })

  const bestOffersQueryParams = computed(() => {
    return removeEmptyValuesInObject({
      offer_type: offerType.value ? String(offerType.value) : null,
    }) as HttpRequestUrlParameters
  })

  const reviewsQueryParams = computed(() => ({
    ...route.query,
    page_size: MAX_REVIEWS_DISPLAYED_IN_LIST,
  }))

  const [
    { data: productResponse, error: productError },
    { data: bestOffersResponse, error: bestOffersError },
    { data: breadcrumbResponse },
    { data: technicalSpecificationsResponse },
    { data: tagsResponse },
    { data: ecoBlockResponse },
    { data: pickersResponse, pending: isPickersResponsePending },
    { data: ratesResponse },
    { data: reviewsResponse, pending: isReviewsResponsePending },
    { data: ratingDistributionResponse },
  ] = await Promise.all([
    // Required calls
    useHttpFetch(getProduct, requestParams),
    useHttpFetch(getBestOffers, {
      ...requestParams,
      queryParams: bestOffersQueryParams,
    }),
    // Optional calls
    useHttpFetch(getBreadcrumb, requestParams),
    useHttpFetch(getTechnicalSpecifications, requestParams),
    useHttpFetch(getSeoTags, {
      queryParams: { model: 'product', pk: productId },
    }),
    useHttpFetch(getEcoBlock, requestParams),
    useHttpFetch(getPickers, {
      ...requestParams,
      queryParams: pickersQueryParams,
    }),
    useHttpFetch(getRating, {
      pathParams: route.params,
    }),
    useHttpFetch(getReviews, {
      pathParams: route.params,
      queryParams: reviewsQueryParams,
    }),
    useHttpFetch(getRatingDistribution, { pathParams: route.params }),
  ])

  // Those 2 endpoints are the only one required to display a viable product page, allowing the user to add a product to its cart,
  // so that's the only 2 that should trigger an error page (404 or 500)
  if (productError.value || bestOffersError.value) {
    throw createError({
      statusCode:
        productError.value?.statusCode || bestOffersError.value?.statusCode,
      statusMessage: i18n(translations.productPageNotFoundError),
      // https://nuxt.com/docs/getting-started/error-handling#createerror
      // If you throw an error created with createError:
      // - on server-side, it will trigger a full-screen error page which you can clear with clearError.
      // - on client-side, it will throw a non-fatal error for you to handle. If you need to trigger a full-screen error page, then you can do this by setting fatal: true.
      fatal: true,
    })
  }

  const isOutOfStock = computed(() => {
    if (!bestOffersResponse.value) {
      return true
    }

    return bestOffersResponse.value.every((offer) => offer.isDisabled)
  })

  const currentGrade = computed(() => {
    if (!bestOffersResponse.value || isOutOfStock.value) {
      return null
    }

    if (grade.value) {
      return bestOffersResponse.value.find(
        (offer) => offer.backboxGrade.value === grade.value,
      )
    }

    return bestOffersResponse.value.find(
      (offer) => offer.isSelectedByDefault === true,
    )
  })

  return {
    productResponse,
    bestOffersResponse,
    breadcrumbResponse,
    technicalSpecificationsResponse,
    tagsResponse,
    ecoBlockResponse,
    pickersResponse,
    isPickersResponsePending,
    ratesResponse,
    reviewsResponse,
    isReviewsResponsePending,
    ratingDistributionResponse,
    isOutOfStock,
    currentGrade,
  }
}
